import { Component, computed, inject, output, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { IpoLanguageService } from '@ip-only/front-end-library';
import { CircleMarker, Layer, Polyline } from 'leaflet';
import { RolloutProgressReportEntities } from '../../../shared/self-inspection-reports/service/entities';
import { RolloutProgressReportsService } from '../../../shared/self-inspection-reports/service/reports/rollout-progress-reports.service';
import { ExistingSpanPipe } from '../../../shared/util/existing-span.pipe';
import { PropertySpanPipe } from '../../../shared/util/property-span.pipe';
import { PointService } from '../../service/map/point/point.service';
import { SpanService } from '../../service/map/span/span.service';
import {
  DistributionPointProperties,
  PointType,
  PopupTypes,
} from '../../span-progress-entities';
import { formatPointTooltip } from '../../utils/point-tooltip.helper';
import {
  findLinkedKp,
  getReportablePointType,
  getReportForMarker,
  ReportFilterType,
} from '../../utils/report-row.helper';
import { MapInteractionService } from '../map-interaction/map-interaction.service';
import { reportsPanelColumns } from './reports-panel-columns';
import { reportsPanelTypeOptions } from './reports-panel-options';
import { ReportsPanelStateService } from './reports-panel-state.service';

interface ReportPanelRow {
  id: string;
  markerId: string;
  name: string;
  reportType: RolloutProgressReportEntities.v3.MarkerTypes;
  status: RolloutProgressReportEntities.v3.ReportStatus;
}

type PointLayer = Layer & {
  feature?: { properties?: DistributionPointProperties };
};

interface KpSelection {
  layer: PointLayer;
  linkedKp: DistributionPointProperties;
}

@Component({
  selector: 'app-reports-panel',
  templateUrl: './reports-panel.component.html',
  styleUrls: ['./reports-panel.component.scss'],
  providers: [ExistingSpanPipe, PropertySpanPipe],
  standalone: false,
})
export class ReportsPanelComponent {
  private readonly reportsService = inject(RolloutProgressReportsService);
  private readonly pointService = inject(PointService);
  private readonly spanService = inject(SpanService);
  private readonly mapInteractionService = inject(MapInteractionService);
  private readonly languageService = inject(IpoLanguageService);
  private readonly existingSpan = inject(ExistingSpanPipe);
  private readonly propertySpan = inject(PropertySpanPipe);
  private readonly state = inject(ReportsPanelStateService);
  private readonly pointLoadingState = toSignal(
    this.pointService.getLoadingState$(),
    { initialValue: null },
  );
  private readonly spanDataVersion = toSignal(this.spanService.spanData$, {
    initialValue: [],
  });

  closeSidePanel = output<void>();

  readonly language$ = this.languageService.getLanguage$();
  readonly columns = reportsPanelColumns;
  readonly reportTypeOptions = reportsPanelTypeOptions;

  readonly selectedReportType = this.state.selectedReportType;
  readonly selectedReportId = signal<string | null>(null);
  readonly searchValue = signal('');
  readonly hideCompletedReports = this.state.hideCompletedReports;

  private readonly allReportRows = computed(() => {
    this.pointLoadingState();
    this.spanDataVersion();
    return this.getAllReportRows();
  });

  readonly availableReportTypeOptions = computed(() => {
    const availableTypes = new Set(
      this.allReportRows().map((row) => row.reportType),
    );

    return this.reportTypeOptions.filter((option) =>
      availableTypes.has(option.value),
    );
  });

  readonly activeReportType = computed(() => {
    const selectedReportType = this.selectedReportType();
    const availableReportTypeOptions = this.availableReportTypeOptions();

    return availableReportTypeOptions.some(
      (option) => option.value === selectedReportType,
    )
      ? selectedReportType
      : (availableReportTypeOptions[0]?.value ?? null);
  });

  readonly reportRows = computed(() => {
    const uniqueRows = this.allReportRows();
    const selectedReportType = this.activeReportType();

    const typeFilteredRows = selectedReportType
      ? uniqueRows.filter((row) => row.reportType === selectedReportType)
      : uniqueRows;
    const statusFilteredRows = this.hideCompletedReports()
      ? typeFilteredRows.filter((row) => {
          const isSpan =
            row.reportType ===
            RolloutProgressReportEntities.v3.MarkerTypes.SPAN;
          const completedStatus = isSpan
            ? RolloutProgressReportEntities.v3.ReportStatus.RESTORED
            : RolloutProgressReportEntities.v3.ReportStatus.DONE;

          return row.status !== completedStatus;
        })
      : typeFilteredRows;

    const searchValue = this.searchValue().trim().toLowerCase();
    if (!searchValue) {
      return statusFilteredRows;
    }

    return statusFilteredRows.filter((row) =>
      row.name.toLowerCase().includes(searchValue),
    );
  });

  onReportTypeSelected(type: string): void {
    this.state.setSelectedReportType(type as ReportFilterType);
  }

  onSearchChanged(value: string): void {
    this.searchValue.set(value);
  }

  onHideCompletedReportsChanged(checked: boolean): void {
    this.state.setHideCompletedReports(checked);
  }

  onReportRowClicked(report: ReportPanelRow): void {
    if (
      report.reportType === RolloutProgressReportEntities.v3.MarkerTypes.SPAN
    ) {
      const spanLayer = this.spanService.getSpanLayersBySpanIds([
        report.markerId,
      ])?.[0] as Polyline | undefined;

      if (spanLayer) {
        this.mapInteractionService.selectSpanFromReportPanel(spanLayer);
        this.selectedReportId.set(report.id);
      }
      return;
    }

    if (report.reportType === RolloutProgressReportEntities.v3.MarkerTypes.HP) {
      const ductpointLayer = this.getDuctpointLayerForHp(report.markerId);

      if (ductpointLayer) {
        this.mapInteractionService.selectPointFromReportPanel(
          ductpointLayer.layer as CircleMarker,
          PopupTypes.Ductpoint,
          ductpointLayer.linkedKp.id,
        );
        this.selectedReportId.set(report.id);
        return;
      }
    }

    const pointLayer = this.getPointLayer(report.reportType, report.markerId);
    if (pointLayer) {
      this.mapInteractionService.onSelectSearchedItem(
        pointLayer as CircleMarker,
      );
      this.selectedReportId.set(report.id);
    }
  }

  markSelectedRow(selectedReportId: string | null) {
    return (report: ReportPanelRow) =>
      report.id === selectedReportId ? { background: '#cdfcfc' } : null;
  }

  private getAllReportRows(): ReportPanelRow[] {
    const spanRows = this.spanService
      .getSpanDataValue()
      .filter(
        (span) =>
          !this.existingSpan.transform(span.properties.accommodationMethodId) &&
          !this.propertySpan.transform(span.properties.permissionTypeId),
      )
      .map((span) =>
        this.toRow(
          RolloutProgressReportEntities.v3.MarkerTypes.SPAN,
          span.properties.spanId,
          span.properties.serialNumber || span.properties.spanId,
        ),
      );
    const pointRows = Object.values(this.pointService.getLayerData())
      .flatMap((layer) => layer.getLayers() as PointLayer[])
      .flatMap((layer) => {
        const properties = layer.feature?.properties;
        const type = properties && getReportablePointType(properties);

        if (!properties || properties.existing || !type) {
          return [];
        }

        const markerId = this.pointService.getKeyForType(type, properties);
        return [
          this.toRow(type, markerId, this.getPointName(type, properties)),
        ];
      });

    return Array.from(
      new Map([...spanRows, ...pointRows].map((row) => [row.id, row])).values(),
    );
  }

  private toRow(
    reportType: ReportFilterType,
    markerId: string,
    name: string,
  ): ReportPanelRow {
    const report = getReportForMarker(
      this.reportsService.storedV3Reports(),
      reportType,
      markerId,
    );
    return {
      id: `${reportType}:${markerId}`,
      markerId,
      name,
      reportType,
      status:
        report?.status ??
        RolloutProgressReportEntities.v3.ReportStatus.NOT_STARTED,
    };
  }

  private getPointName(
    type: ReportFilterType,
    properties: DistributionPointProperties,
  ): string {
    if (type === RolloutProgressReportEntities.v3.MarkerTypes.HP) {
      const address = properties.name.replace(/\s+\d{5}\s+.*$/, '');
      return address;
    }

    if (properties.telematorId) {
      return formatPointTooltip(
        `${properties.name} (${properties.telematorId})`,
      );
    }

    return properties.name || properties.id;
  }

  private getPointLayer(
    type: RolloutProgressReportEntities.v3.MarkerTypes,
    markerId: string,
  ): PointLayer | undefined {
    return Object.values(this.pointService.getLayerData())
      .flatMap((layer) => layer.getLayers() as PointLayer[])
      .find((layer) => {
        const properties = layer.feature?.properties;
        return (
          properties !== undefined &&
          this.pointService.getKeyForType(type, properties) === markerId
        );
      });
  }

  private getDuctpointLayerForHp(hpMarkerId: string): KpSelection | undefined {
    const pointLayerData = this.pointService.getLayerData();
    const ductpointLayers = ['KP', 'SDP'].flatMap((type) =>
      pointLayerData[type]
        ? (pointLayerData[type].getLayers() as PointLayer[])
        : [],
    );

    return ductpointLayers
      .map((layer) => ({
        layer,
        linkedKp: findLinkedKp(layer.feature?.properties, hpMarkerId),
      }))
      .find(
        (selection): selection is KpSelection =>
          selection.linkedKp !== undefined,
      );
  }

  onReportMenuClicked(e: MouseEvent | Event): void {
    e.stopPropagation();
    this.closeSidePanel.emit();
  }
}
